import {
  PropsWithChildren,
  useState,
  useContext,
  createContext,
  useEffect,
} from 'react';
import { useFetch } from './FetchProvider';
import { PlaylistSearchHitType } from '../types/search';
import { useUserContext } from './UserContext';
import { SEARCH_PATH, SEARCH_TERM } from '../constants';
import { track } from '../lib/util/tracking';
import { getFormattedQuery } from '../lib/util/query';
import { getUrl } from '../lib/frontend/search/url';
import { FilterType } from '@osmosis/shared/types/search';
import { SearchFilter } from '@osmosis/shared/types/search';
import { SearchFilters } from '@osmosis/shared/types/search';
import { SubProduct } from '@osmosis/shared/types/user';
import { SubProductState } from '@osmosis/shared/types/user';
import { UserProps } from '@osmosis/shared/types/user';
import { UserSearchFilter } from '@osmosis/shared/types/user';

import { IntlShape, useIntl } from 'react-intl';
import { useFeatureFlagsContext } from './FeatureFlagsContext';
import { usePagesContext } from './PagesProvider';
import { useLanguageContext } from './LanguageProvider';
import { useEmbedContext } from '@osmosis/nextjs-components/context/EmbedProvider';

type SearchContextValues = {
  isSearchResultsOpen: boolean;
  openSearchResults: () => void;
  closeSearchResults: () => void;
  search: string;
  setSearchValue: (value: string) => void;
  setSearchQuery: (value?: string) => void;
  clearSearch: () => void;
  searchResults: PlaylistSearchHitType[] | undefined;
  fetchSearchResults: (
    value: string,
    searchFilter?: SearchFilters | null,
  ) => void;
  fetchAutocomplete: (route?: string) => void;
  autocompleteResults: string[];
  searchResultsIsLoading: boolean;
  isSearchFieldFocused: boolean;
  searchFilter: SearchFilters;
  isFilterAvailable: boolean;
  setSearchFilter: (filters: SearchFilters) => Promise<void>;
  clearSearchFilter: () => Promise<void>;
  handleSearchFieldFocus: (value: boolean) => void;
};

const SearchContext = createContext<SearchContextValues>(
  {} as SearchContextValues,
);

type Props = Record<string, unknown>;

const EMPTY_FILTER: UserSearchFilter = { subProducts: [] };

const getQueryWithoutSearch = () => {
  const url = getUrl();
  url.searchParams.delete(SEARCH_TERM);

  return [...url.searchParams.entries()].reduce(
    (obj, [key, value]) => ({
      ...obj,
      [key]: value,
    }),
    {},
  );
};

const searchFilterToRequest = (searchFilter?: SearchFilters | null) => {
  if (!searchFilter) return [];
  const params = Object.entries(searchFilter).reduce(
    (result: string[], [filterName, filter]: [string, SearchFilter]) => {
      filter?.options?.forEach(({ isSelected, label, isDefault }) => {
        if (!isSelected || isDefault) return;
        result.push(`${filterName}::${label}`);
      });

      return result;
    },
    [],
  );

  return params;
};

const getFilters = (
  subProducts: SubProduct[],
  savedUserFilter: UserSearchFilter,
  intl: IntlShape,
): SearchFilters => {
  const options =
    subProducts?.map(({ groupName, state }) => {
      const label =
        groupName === 'Basic Sciences'
          ? intl.formatMessage({
              defaultMessage: 'Basic Sciences',
              id: 'common.basicSciences',
            })
          : groupName === 'Clinical Sciences'
            ? intl.formatMessage({
                defaultMessage: 'Clinical Sciences',
                id: 'common.clinicalSciences',
              })
            : groupName;
      const isSelected =
        savedUserFilter?.subProducts?.includes(groupName) || false;

      return {
        id: groupName.replace(/ /g, '_').toLowerCase(),
        label,
        isSelected,
        lockState: state,
      };
    }) || [];
  const labelDescription = subProducts.some(
    ({ state }) => state === SubProductState.locked,
  )
    ? intl.formatMessage({
        id: 'search.filter.contains_locked_content',
        defaultMessage: '(Contains locked content)',
      })
    : '';
  const subProductsFilter: SearchFilter = {
    type: FilterType.RADIO,
    name: 'Packages',
    options: [
      {
        id: 'all',
        label: intl.formatMessage({
          id: 'common.all',
          defaultMessage: 'All',
        }),
        labelDescription,
        isSelected: !options.some(({ isSelected }) => isSelected),
        lockState: SubProductState.active,
        isDefault: true,
      },
      ...options,
    ],
  };

  return {
    subProducts: subProductsFilter,
  };
};

const getInitialFilter = (
  user: UserProps | null,
  intl: IntlShape,
): SearchFilters => {
  return getFilters(
    user?.subProducts || [],
    user?.profile?.searchFilter || EMPTY_FILTER,
    intl,
  );
};

const getUrlWithSearch = (value?: string) => {
  const withoutSearch = getQueryWithoutSearch();
  let queryParams = {};
  if (value) {
    queryParams = {
      ...withoutSearch,
      [SEARCH_TERM]: value,
    };
  } else {
    queryParams = withoutSearch;
  }

  const url = getUrl();

  // Manually create search params because URL api encodes the values
  const searchParams = Object.entries(queryParams).reduce(
    (str, [key, v], i) => `${str}${i > 0 ? '&' : ''}${key}=${v}`,
    '',
  );

  return `${url.pathname}${searchParams ? '?' : ''}${searchParams}`;
};

/**
 * SearchProvider.
 */
const SearchProvider = (props: PropsWithChildren<Props>): JSX.Element => {
  const intl = useIntl();

  const { children } = props;

  const { get, post } = useFetch();
  const { user } = useUserContext();
  const { activeLanguage } = useLanguageContext();

  const { isActive } = useFeatureFlagsContext();

  const { push, query, params } = usePagesContext();

  const { getPageLocation, navigateToRoute } = useEmbedContext();

  const searchedValue = getFormattedQuery(query?.[SEARCH_TERM]);

  const language = params?.lang || activeLanguage;

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<PlaylistSearchHitType[]>(
    [],
  );
  const [autocompleteResults, setAutocompleteResults] = useState<string[]>([]);
  const [searchResultsIsLoading, setSearchResultIsLoading] =
    useState(!!searchedValue);
  const [isSearchResultsOpen, setIsSearchResultsOpen] =
    useState(!!searchedValue);
  const [isSearchFieldFocused, setSearchFieldFocused] = useState(false);

  const [searchFilters, setSearchFilters] = useState<SearchFilters>(
    getInitialFilter(user, intl),
  );

  const { pathname } = getPageLocation();

  const isSearchPage = pathname === SEARCH_PATH;

  const isFilterAvailable =
    isActive('filterable-search') && (user?.subProducts?.length || 0) > 1;

  const openSearchResults = () => {
    if (isSearchPage) {
      setIsSearchResultsOpen(true);
    }
  };
  const closeSearchResults = () => {
    setSearch('');
    setIsSearchResultsOpen(false);
    clearSearch();
    handleClearSearchQuery();
  };

  const handleClearSearchQuery = () => {
    // Get native URL object to avoid encoded strings with router.push
    const url = getUrlWithSearch();
    push(url);
  };

  const handleSetSearchQuery = (searchValue?: string) => {
    if (!searchValue) {
      setSearch('');
      setSearchResults([]);
      if (!isSearchResultsOpen) openSearchResults();

      return;
    }

    const formattedValue = searchValue.trim().replace(/ /g, '_');
    if (searchedValue !== formattedValue) {
      if (formattedValue) {
        setSearchResultIsLoading(true);

        if (isSearchPage) {
          // Get native URL object to avoid encoded strings with router.push
          const url = getUrlWithSearch(formattedValue);
          window.history.pushState({}, '', url);
        }
        // send original value to search because the formatted one doesn't return the right results
        fetchSearchResults(searchValue, searchFilters);
      } else {
        handleClearSearchQuery();
      }
    }
  };

  const clearSearch = () => {
    setSearch('');
    setAutocompleteResults([]);
  };

  const setSearchValue = (value: string) => {
    if (!value) {
      clearSearch();

      return;
    }
    setSearch(value);
  };

  const fetchSearchResults = async (
    searchValue: string,
    searchFilter?: SearchFilters | null,
  ) => {
    if (!isSearchResultsOpen) openSearchResults();

    if (searchValue.length < 2) {
      setAutocompleteResults([]);
      setSearchResults([]);
      setSearchResultIsLoading(false);

      return;
    }

    setSearchResultIsLoading(true);

    const url = `${SEARCH_PATH}?${new URLSearchParams({
      [SEARCH_TERM]: searchValue,
    })}`;

    if (!isSearchPage) {
      navigateToRoute(url);
      return;
    }

    const { data } = await get<{ hits: PlaylistSearchHitType[] }>(
      '/data/search',
      {
        params: {
          q: searchValue,
          filter: undefined,
          language,
          watchStatus: true,
          searchFilters: isFilterAvailable
            ? searchFilterToRequest(searchFilter)
            : [],
        },
      },
    );

    track(
      'search',
      {
        action: 'search',
        query: searchValue,
        top_results: data.hits.length
          ? data.hits.slice(0, 10).map((h) => h.concept)
          : null,
      },
      ['keen'],
    );

    setSearchResults(data.hits);
    setSearchResultIsLoading(false);
  };

  const fetchAutocomplete = async (route?: string) => {
    if (search?.length < 2) {
      setAutocompleteResults([]);

      return;
    }

    const { data }: { data: string[] } = await get(
      route || '/data/searchAutocomplete',
      {
        params: {
          q: search,
          filter: undefined,
          language,
        },
      },
    );

    setAutocompleteResults(data);
  };

  const handleSearchFieldFocus = (state: boolean) => {
    setSearchFieldFocused(state);
  };

  const updateUserFilter = async (newFilters: SearchFilters) => {
    const searchFilter: UserSearchFilter = { subProducts: [] };
    newFilters.subProducts.options.forEach(
      ({ isSelected, label, isDefault }) => {
        if (isDefault || !isSelected) return;
        searchFilter.subProducts.push(label);
      },
    );

    await post('/v0/user', {
      searchFilter,
    });
  };

  const handleSetSearchFilter = async (searchFilters: SearchFilters) => {
    setSearchResultIsLoading(true);

    setSearchFilters(searchFilters);
    updateUserFilter(searchFilters);
  };

  const clearSearchFilter = async () => {
    const emptyFilter = getFilters(user?.subProducts || [], EMPTY_FILTER, intl);
    setSearchResultIsLoading(true);
    setSearchFilters(emptyFilter);
    updateUserFilter(emptyFilter);
  };

  useEffect(() => {
    if (searchedValue === undefined && search === '') {
      if (isSearchResultsOpen) closeSearchResults();
    } else {
      if (searchedValue || search) {
        const searchValue = searchedValue || search;
        const formattedValue = searchValue.replace(/_/g, ' ');
        setSearchValue(formattedValue);
        fetchSearchResults(formattedValue, searchFilters);
      } else {
        handleClearSearchQuery();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedValue, searchFilters]);

  useEffect(() => {
    if (user) {
      setSearchFilters(getInitialFilter(user, intl));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <SearchContext.Provider
      value={{
        fetchAutocomplete,
        autocompleteResults,
        isSearchResultsOpen,
        openSearchResults,
        closeSearchResults,
        search,
        clearSearch,
        setSearchValue,
        setSearchQuery: handleSetSearchQuery,
        searchResults,
        fetchSearchResults,
        searchResultsIsLoading,
        isSearchFieldFocused,
        isFilterAvailable,
        searchFilter: searchFilters,
        clearSearchFilter,
        setSearchFilter: handleSetSearchFilter,
        handleSearchFieldFocus,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

/**
 * Returns values of SearchContext.
 */
export const useSearchContext = () => useContext(SearchContext);

export default SearchProvider;
